// Per-employee task switches — client copy of server/utils/tasks.js.
// Keep TASK_GROUPS / ROLE_DEFAULT_TASKS in sync with the server; the server is
// what actually enforces them, this only decides what the app shows.
//
// Overrides live in user.permissions as "+task" (switched on by the admin) and
// "-task" (switched off). Without overrides an employee gets their role's
// defaults, which mirror exactly what that role could do before switches.

export const TASK_GROUPS = [
  { id: 'sales',   tasks: ['sales.create', 'sales.deliver'] },
  { id: 'cashier', tasks: ['cashier.payments'] },
  { id: 'store',   tasks: ['store.analytics', 'store.team', 'store.stock', 'store.packages'] },
  { id: 'central', tasks: ['central.frames', 'central.lenses', 'central.transfers', 'central.supplier', 'central.supplies'] },
  { id: 'lab',     tasks: ['lab.work', 'lab.manage'] },
];

export const TASKS = TASK_GROUPS.flatMap((g) => g.tasks);

export const CENTRAL_TASKS = TASK_GROUPS.find((g) => g.id === 'central').tasks;
export const STORE_DASHBOARD_TASKS = ['cashier.payments', 'store.analytics', 'store.team', 'store.stock', 'store.packages'];
export const LAB_TASKS = ['lab.work', 'lab.manage'];

const CENTRAL_ALL = CENTRAL_TASKS;

export const ROLE_DEFAULT_TASKS = {
  sales_person:       ['sales.create', 'sales.deliver'],
  store_manager:      ['sales.create', 'sales.deliver', 'cashier.payments', 'store.analytics', 'store.team', 'store.stock', 'store.packages'],
  inventory_manager:  CENTRAL_ALL,
  operations_manager: CENTRAL_ALL,
  courier:            [],
  technician:         ['lab.work'],
  lab_manager:        ['lab.work', 'lab.manage'],
};

// Advisors see every screen an admin sees; the server only lets their changes
// through for the admin-dashboard sections the admin put on Edit.
const isAdminRole = (role) => role === 'admin' || role === 'super_admin' || role === 'advisor';

export function roleDefaultTasks(role) {
  return ROLE_DEFAULT_TASKS[role] || [];
}

export function taskOverrides(permissions) {
  const granted = [];
  const removed = [];
  for (const p of permissions || []) {
    const s = String(p);
    const t = s.slice(1);
    if (!TASKS.includes(t)) continue;
    if (s.startsWith('+')) granted.push(t);
    else if (s.startsWith('-')) removed.push(t);
  }
  return { granted, removed };
}

export function effectiveTasks(user) {
  if (!user) return [];
  if (isAdminRole(user.role)) return [...TASKS];
  const { granted, removed } = taskOverrides(user.permissions);
  const out = new Set(roleDefaultTasks(user.role).filter((t) => !removed.includes(t)));
  granted.forEach((t) => out.add(t));
  return TASKS.filter((t) => out.has(t));
}

export function hasTask(user, task) {
  return effectiveTasks(user).includes(task);
}

export function hasAnyTask(user, tasks) {
  const eff = effectiveTasks(user);
  return tasks.some((t) => eff.includes(t));
}

// Same rule the server's requireTask() uses: holding any of `tasks` lets you
// in; otherwise a role in `roles` still gets in when none of its default
// tasks are among `tasks` (so screens keep working for roles the switches
// don't cover). `roles` null = every signed-in role.
export function canAccess(user, tasks, roles = null) {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  const list = Array.isArray(tasks) ? tasks : [tasks];
  if (hasAnyTask(user, list)) return true;
  const roleAllowed = roles === null || roles.includes(user.role);
  const roleCovers = roleDefaultTasks(user.role).some((t) => list.includes(t));
  return roleAllowed && !roleCovers;
}

// The signed-in user as stored at login (kept fresh by refreshStoredUser).
export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}
